import { Phone, Email } from "@mui/icons-material";
import LoginPhoneIdentifier from "./LoginPhoneIdentifier";
import LoginEmailIdentifier from "./LoginEmailIdentifier";
import SupportedAuthProviders from "./MicrospecSupportedAuthProvider";

/**
 * Comprehensive provider configuration that defines everything needed for each provider
 * This centralizes all provider-specific configuration in one place
 */
const PROVIDER_REGISTRY = {
  [SupportedAuthProviders.PHONE]: {
    // Button configuration
    icon: Phone,
    translationKey: "phone",
    fallbackText: "هاتف", // Arabic fallback
    
    // Component configuration
    identifierComponent: LoginPhoneIdentifier,
    
    // Provider-specific props that should be passed to the identifier component
    getIdentifierProps: (baseProps) => ({
      ...baseProps,
      // Phone-specific props
      onSetCountryCode: baseProps.onSetCountryCode,
      enableRegister: baseProps.enableRegister
    }),
    
    // Validation rules (for future use)
    validation: {
      required: true,
      pattern: /^[\d\s\-+()]+$/,
      minLength: 10
    }
  },
  
  [SupportedAuthProviders.EMAIL]: {
    // Button configuration
    icon: Email,
    translationKey: "email",
    fallbackText: "إيميل", // Arabic fallback
    
    // Component configuration
    identifierComponent: LoginEmailIdentifier,
    
    // Provider-specific props
    getIdentifierProps: (baseProps) => ({
      ...baseProps,
      // Pass enableRegister to email identifier
      enableRegister: baseProps.enableRegister
    }),
    
    // Validation rules (for future use)
    validation: {
      required: true,
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      minLength: 5
    }
  }
  
  // Future providers can be easily added here with full configuration:
  // [SupportedAuthProviders.GOOGLE]: {
  //   icon: Google,
  //   translationKey: "google",
  //   fallbackText: "Google",
  //   identifierComponent: LoginGoogleIdentifier,
  //   getIdentifierProps: (baseProps) => ({ ...baseProps }),
  //   validation: { required: false }
  // },
  
  // [SupportedAuthProviders.FACEBOOK]: {
  //   icon: Facebook,
  //   translationKey: "facebook",
  //   fallbackText: "Facebook", 
  //   identifierComponent: LoginFacebookIdentifier,
  //   getIdentifierProps: (baseProps) => ({ ...baseProps }),
  //   validation: { required: false }
  // },
  
  // [SupportedAuthProviders.APPLE]: {
  //   icon: Apple,
  //   translationKey: "apple",
  //   fallbackText: "Apple",
  //   identifierComponent: LoginAppleIdentifier,
  //   getIdentifierProps: (baseProps) => ({ ...baseProps }),
  //   validation: { required: false }
  // }
};

/**
 * Provider Registry Class - Manages all provider-related operations
 */
class ProviderRegistry {
  /**
   * Gets the configuration for a specific provider
   * @param {string} provider - The provider type
   * @returns {Object|null} - The provider configuration or null if not found
   */
  static getConfig(provider) {
    return PROVIDER_REGISTRY[provider] || null;
  }

  /**
   * Checks if a provider is registered
   * @param {string} provider - The provider type to check
   * @returns {boolean} - True if the provider is registered
   */
  static isRegistered(provider) {
    return provider in PROVIDER_REGISTRY;
  }

  /**
   * Gets all registered provider types
   * @returns {Array} - Array of all registered provider types
   */
  static getAllProviders() {
    return Object.keys(PROVIDER_REGISTRY);
  }

  /**
   * Filters enabled providers to only include registered ones
   * @param {Array} enabledProviders - Array of enabled provider types
   * @returns {Array} - Array of enabled providers that are also registered
   */
  static filterRegisteredProviders(enabledProviders) {
    return enabledProviders.filter(provider => this.isRegistered(provider));
  }

  /**
   * Gets validation rules for a provider
   * @param {string} provider - The provider type
   * @returns {Object|null} - Validation rules or null if provider not found
   */
  static getValidationRules(provider) {
    const config = this.getConfig(provider);
    return config ? config.validation : null;
  }

  /**
   * Validates an identifier value against provider rules
   * @param {string} provider - The provider type
   * @param {string} value - The identifier value to validate
   * @returns {Object} - Validation result { isValid: boolean, errors: Array }
   */
  static validateIdentifier(provider, value) {
    const rules = this.getValidationRules(provider);
    if (!rules) {
      return { isValid: true, errors: [] };
    }

    const errors = [];

    if (rules.required && (!value || value.trim().length === 0)) {
      errors.push('This field is required');
    }

    if (value && rules.pattern && !rules.pattern.test(value)) {
      errors.push('Invalid format');
    }

    if (value && rules.minLength && value.length < rules.minLength) {
      errors.push(`Minimum length is ${rules.minLength} characters`);
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

// Export the registry class as default
export default ProviderRegistry;